import type { ReactNode } from 'react'
import { CookieManager } from 'react-cookie-manager'
import { cookieConsentTranslations } from '~/lib/cookie-consent/translations'

interface CookieConsentProviderProps {
    children: ReactNode
}

/**
 * UK GDPR and PECR compliant cookie consent provider
 *
 * Wraps the application with react-cookie-manager configured for:
 * - Popup display (bottom-right corner)
 * - UK regulatory compliance
 * - Granular analytics consent
 * - Automatic tracking prevention
 */
export function CookieConsentProvider({ children }: CookieConsentProviderProps) {
    return (
        <CookieManager
            // Display configuration
            displayType="popup" // Bottom-right popup (non-blocking)
            theme="dark"
            showManageButton={true}
            enableFloatingButton={false} // Disabled - using footer link instead
            // Cookie configuration
            cookieKey="entx_cookie_consent"
            expirationDays={365}
            // Translations (UK GDPR compliant wording)
            translations={cookieConsentTranslations}
            privacyPolicyUrl="/cookies"
            // Cookie categories
            // Analytics: Optional analytics tracking
            // Social: Third-party embeds (Spotify, Google Maps)
            cookieCategories={{
                Analytics: false,
                Social: true,
                Advertising: false,
            }}
            // Initial preferences (all non-essential cookies default to OFF)
            initialPreferences={{
                Analytics: false,
                Social: false,
                Advertising: false,
            }}
            // Geolocation is disabled to support E2E testing
            // TODO: Re-enable geolocation for production (banner should only show in UK/EU/EEA)
            // disableGeolocation={false}
            disableGeolocation={true}

            // Disable automatic iframe/script blocking - we manually control embeds based on consent
            // This prevents the library from interfering with our custom consent checks
            disableAutomaticBlocking={true}

            // Hide "Powered by CookieKit" branding
            classNames={{
                poweredByLink: 'hidden',
            }}
        >
            {children}
        </CookieManager>
    )
}
